import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TechnicalSheetForm({
  open,
  onClose,
  onSave,
  initialData = null,
  isLoading = false,
}) {
  const defaultForm = {
    commercial_name: "",
    brand: "",
    manufacturer: "",
    description: "",
    ingredients_text: "",
    allergens: "",
    net_weight: "",
    format_description: "",
    storage_conditions: "",
    shelf_life: "",
    origin_country: "",
    product_code: "",
    observations: "",
    nutritional_values_json: "",
  };

  const [form, setForm] = useState(initialData || defaultForm);

  React.useEffect(() => {
    if (initialData) {
      setForm({ ...defaultForm, ...initialData });
    } else {
      setForm(defaultForm);
    }
  }, [initialData, open]);

  const handleSave = () => {
    if (!form.commercial_name) {
      toast.warning("El nom comercial és obligatori");
      return;
    }
    onSave(form);
    handleClose();
  };

  const handleClose = () => {
    setForm(initialData || defaultForm);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fitxa tècnica del producte</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold">Nom comercial *</Label>
            <Input
              placeholder="Ex: Mozzarella fresca"
              value={form.commercial_name}
              onChange={(e) => setForm({ ...form, commercial_name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Marca</Label>
            <Input
              placeholder="Ex: Galbani"
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Fabricant</Label>
            <Input
              placeholder="Ex: Galbani SpA"
              value={form.manufacturer}
              onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Codi producte</Label>
            <Input
              placeholder="Ex: EAN 8001015000084"
              value={form.product_code}
              onChange={(e) => setForm({ ...form, product_code: e.target.value })}
            />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold">Descripció</Label>
            <Textarea
              placeholder="Descripció general del producte"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold">Ingredients / Composició</Label>
            <Textarea
              placeholder="Llistar tots els ingredients"
              value={form.ingredients_text}
              onChange={(e) => setForm({ ...form, ingredients_text: e.target.value })}
              rows={2}
            />
          </div>

          <div>
            <Label className="text-xs font-semibold">Al·lèrgens</Label>
            <Input
              placeholder="Ex: Làctic, Gluten"
              value={form.allergens}
              onChange={(e) => setForm({ ...form, allergens: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Pes net / Format</Label>
            <Input
              placeholder="Ex: 250g"
              value={form.net_weight}
              onChange={(e) => setForm({ ...form, net_weight: e.target.value })}
            />
          </div>

          <div>
            <Label className="text-xs font-semibold">Conservació</Label>
            <Input
              placeholder="Ex: 2-8°C"
              value={form.storage_conditions}
              onChange={(e) => setForm({ ...form, storage_conditions: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Vida útil</Label>
            <Input
              placeholder="Ex: 30 dies"
              value={form.shelf_life}
              onChange={(e) => setForm({ ...form, shelf_life: e.target.value })}
            />
          </div>

          <div>
            <Label className="text-xs font-semibold">País origen</Label>
            <Input
              placeholder="Ex: Itàlia"
              value={form.origin_country}
              onChange={(e) => setForm({ ...form, origin_country: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Format descripció</Label>
            <Input
              placeholder="Ex: Bloc de 500g"
              value={form.format_description}
              onChange={(e) => setForm({ ...form, format_description: e.target.value })}
            />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold">Observacions</Label>
            <Textarea
              placeholder="Notes addicionals"
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel·lar</Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !form.commercial_name}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar fitxa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}